import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Download, Share2, Palette, Sigma, Timer, CalendarDays, IndianRupee, FileText, MessageCircle, Layers } from "lucide-react";
import { toast } from "sonner";
import { api, absoluteUrl, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function Results() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const [design, setDesign] = useState(null);
  const [quote, setQuote] = useState(null);
  const [finalPrice, setFinalPrice] = useState("");

  useEffect(() => {
    api.get(`/designs/${id}`).then(({ data }) => {
      setDesign(data.design);
      setQuote(data.quote);
      setFinalPrice(String(data.quote?.final_price ?? ""));
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [id]);

  const savePrice = async () => {
    try {
      const v = parseFloat(finalPrice);
      if (!(v > 0)) return;
      const { data } = await api.put(`/quotes/${quote.id}`, { final_price: v });
      setQuote(data);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const download = async () => {
    try {
      const token = localStorage.getItem("sq_token");
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/designs/${id}/stitch-file`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("download failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${design.design_name.replace(/\s+/g, "_")}.${design.output_format.toLowerCase()}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("sq_token");
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${quote.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("pdf failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${design.design_name.replace(/\s+/g, "_")}_quote.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("PDF failed");
    }
  };

  const share = async () => {
    try {
      const { data } = await api.post(`/quotes/${quote.id}/share`);
      const url = `${window.location.origin}/share/${data.share_token}`;
      await navigator.clipboard.writeText(url);
      toast.success(t("shareCopied"));
      return url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const shareWhatsapp = async () => {
    try {
      const { data } = await api.post(`/quotes/${quote.id}/share`);
      const url = `${window.location.origin}/share/${data.share_token}`;
      const msg = t("waMessage")
        .replace("{business}", "our unit")
        .replace("{price}", Math.round(quote.final_price).toLocaleString("en-IN"))
        .replace("{date}", quote.delivery_date)
        .replace("{url}", url);
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!design || !quote) return <div className="py-16 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5">
      <button onClick={() => nav(-1)} className="text-sm text-muted-foreground flex items-center gap-1">
        <ArrowLeft size={14} /> {t("back")}
      </button>

      {/* Header */}
      <div className="sq-card p-4 flex gap-4 items-center">
        <img src={absoluteUrl(design.image_url)} alt="" className="w-20 h-20 rounded-lg object-cover bg-muted" />
        <div className="flex-1 min-w-0">
          <div className="sq-label">{t("resultsTitle")}</div>
          <div className="font-heading font-bold text-xl truncate">{design.design_name}</div>
          <div className="text-xs text-muted-foreground truncate">
            {design.garment_type} · {design.machine_brand} · {design.fabric_type}
          </div>
        </div>
      </div>

      {/* Stitch details */}
      <section className="sq-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-semibold text-lg">{t("stitchDetails")}</h2>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{design.output_format}</span>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <MetricBig icon={<Sigma size={14} />} label={t("stitchCount")}
            value={design.stitch_count.toLocaleString("en-IN")} testId={TID.resultsStitchCount} />
          <MetricBig icon={<Timer size={14} />} label={t("estMachineTime")}
            value={`${design.machine_time_minutes} ${t("minutes")}`} testId={TID.resultsMachineTime} />
          <MetricBig icon={<Palette size={14} />} label={t("threadColors")}
            value={`${design.thread_colors} ${t("colors")}`} testId={TID.resultsColors} />
          <MetricBig icon={<Download size={14} />} label={t("format")} value={design.output_format}
            testId={TID.resultsFormat} />
        </div>

        {/* Machine area badge */}
        {design.machine_area && design.num_heads > 1 && (
          <div className="mt-4 flex items-center gap-2 rounded-lg bg-muted/40 border border-border px-4 py-3">
            <Layers size={16} className="text-primary shrink-0" />
            <div className="flex-1 text-sm">
              <span className="font-semibold">{design.machine_area.replace("x", " × ")}mm</span>
              <span className="text-muted-foreground ml-2">
                — {design.num_heads} {t("heads")} · {t("machineAreaResultNote").replace("{n}", design.num_heads)}
              </span>
            </div>
          </div>
        )}
        <Button data-testid={TID.downloadStitchBtn} onClick={download} variant="secondary"
          className="mt-5 h-12 w-full font-semibold">
          <Download size={16} className="mr-2" /> {t("downloadFile")}
        </Button>
      </section>

      {/* Bits / embellishments breakdown */}
      {design.bits_detail && Object.keys(design.bits_detail).length > 0 && (
        <section className="sq-card p-5">
          <h2 className="font-heading font-semibold text-lg mb-3">{t("bitsBreakdown")}</h2>
          <div className="space-y-2">
            {Object.entries(design.bits_detail).map(([key, val]) => {
              if (!val || (!val.count && !val.grams)) return null;
              return (
                <div key={key}
                  className="flex items-center justify-between py-2 border-b border-dashed border-border last:border-0">
                  <span className="font-medium capitalize">{t(key) || key.replace(/_/g, " ")}</span>
                  <div className="text-right text-sm text-muted-foreground space-x-2">
                    {val.count ? <span className="font-semibold text-foreground">{Number(val.count).toLocaleString("en-IN")} pcs</span> : null}
                    {val.grams ? <span>~{val.grams} g</span> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Quote */}
      <section className="sq-card p-5 relative overflow-hidden">
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-primary/5" />
        <div className="flex items-center justify-between mb-4 relative">
          <h2 className="font-heading font-semibold text-lg">{t("priceQuote")}</h2>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <CalendarDays size={12} /> <span data-testid={TID.deliveryDate}>{quote.delivery_date}</span>
          </span>
        </div>

        <div className="relative space-y-2">
          <Row label={t("materialCost")} value={inr(quote.material_cost)} testId={TID.materialCost} />
          <Row
            label={t("stitchMeterCost")}
            value={inr(quote.stitch_meter_cost)}
            testId={TID.machineCost}
          />
          {quote.heads_per_meter != null && (
            <div className="text-xs text-muted-foreground rounded-lg bg-muted/40 border border-border p-3 space-y-1">
              <div>{t("meterFormulaTitle")}</div>
              <div className="font-mono text-[11px]">
                ({Number(design.stitch_count).toLocaleString("en-IN")} ÷ 1000) × {quote.heads_per_meter} × {quote.stitch_rate_paisa} = ₹{Number(quote.stitch_meter_cost || 0).toFixed(2)}
              </div>
              <div>
                {t("headsPerMeter")}: {quote.heads_per_meter} ({quote.frame_mm || design.frame_mm}mm → 1000÷{quote.frame_mm || design.frame_mm})
              </div>
              {quote.price_per_meter != null && (
                <div>{t("pricePerMeter")}: {inr(quote.price_per_meter)}</div>
              )}
            </div>
          )}
          <div className="border-t border-dashed border-border my-3" />
          <div className="flex items-baseline justify-between">
            <span className="sq-label">{t("suggestedPrice")}</span>
            <span className="sq-stat text-4xl text-primary" data-testid={TID.suggestedPrice}>{inr(quote.suggested_price)}</span>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-[1fr_auto] gap-2">
          <div>
            <label className="sq-label flex items-center gap-1"><IndianRupee size={12} />{t("finalPrice")}</label>
            <Input data-testid={TID.finalPriceInput} type="number" className="h-12 mt-1.5"
              value={finalPrice} onChange={(e) => setFinalPrice(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button data-testid={TID.savePriceBtn} onClick={savePrice} variant="outline" className="h-12">
              {t("savePrice")}
            </Button>
          </div>
        </div>

        <Button data-testid={TID.shareBtn} onClick={share}
          className="mt-5 h-14 w-full text-lg font-semibold sq-tap">
          <Share2 size={18} className="mr-2" /> {t("shareQuote")}
        </Button>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button
            data-testid={TID.waShareBtn}
            onClick={shareWhatsapp}
            variant="outline"
            className="h-12 border-success/40 text-success hover:bg-success/10"
          >
            <MessageCircle size={16} className="mr-2" /> {t("shareWhatsapp")}
          </Button>
          <Button
            data-testid={TID.downloadPdfBtn}
            onClick={downloadPdf}
            variant="outline"
            className="h-12"
          >
            <FileText size={16} className="mr-2" /> {t("downloadPdf")}
          </Button>
        </div>
      </section>
    </div>
  );
}

function MetricBig({ icon, label, value, testId }) {
  return (
    <div className="rounded-xl bg-muted/40 p-4">
      <div className="sq-label flex items-center gap-1">{icon}{label}</div>
      <div className="sq-stat text-2xl mt-1" data-testid={testId}>{value}</div>
    </div>
  );
}

function Row({ label, value, testId }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold" data-testid={testId}>{value}</span>
    </div>
  );
}
